import {
  ANYWHERE_IN_INDIA,
  type CartEntry,
  formatIsoDate,
  formatMoney,
  isCountryWide,
  isoFromMillis,
  lastPaid,
  type Paid,
  type ShopLocation,
  searchQuery,
  sellerName,
  storeLinks,
} from "@larder/shared";
import { useState } from "react";
import { NutritionPanel } from "../components/NutritionPanel";
import { OfferCards } from "../components/OfferCards";
import { PriceIntro } from "../components/PriceIntro";
import { StoreMark } from "../components/StoreMark";
import { findWhere, introSeen, markIntroSeen } from "../data/prices";
import { type Known, useProductFacts } from "../data/useProductFacts";
import { useShopPrices } from "../data/useShopPrices";

/**
 * Opening something on the shopping list.
 *
 * This screen used to be the add form, prefilled: tapping a line went straight
 * to "Good until" and "Where does it go?" for a thing nobody had bought yet.
 * Both questions are real and both are premature — a shelf and a date are
 * facts about a packet in your hand, and the packet is still in the shop.
 *
 * The question here is the one the list itself raises: how do I get this, and
 * for how much? So the screen is what the thing IS, what five shops charge for
 * it, which is cheapest, and the two things that can happen to a line on a list
 * — it arrives, or you change your mind. The date and the shelf are asked
 * afterwards, by the add form, at the moment they are answerable.
 *
 * The store buttons are ordinary links on purpose (ADR 0023): every address is
 * claimed by its own app on both platforms, so the app opens when it is
 * installed and the mobile site opens when it is not.
 *
 * Prices are a good-to-have and everything here is built to survive their
 * absence (ADR 0024). Without a lookup this is a title, five buttons and what
 * you last paid — which is a finished screen, not a broken one.
 */
export function CartDetail({
  entry,
  items,
  timezone,
  householdId,
  where,
  busy,
  onGot,
  onDrop,
  onFoundWhere,
  onClose,
}: {
  entry: CartEntry;
  /**
   * Everything the household has ever had. Two jobs: what this cost last
   * time, and the macros of a packet they have owned before.
   */
  items: readonly (Paid & Known)[];
  timezone: string;
  /** Which household this line belongs to; the price intro is answered per household. */
  householdId: string;
  /** The household's city, once someone has answered. Null means nobody has. */
  where: ShopLocation | null;
  busy: boolean;
  /** It's in the house now — hands over to the add form. */
  onGot: () => void;
  onDrop: () => void;
  /** Remembers the answer on the household, so this is asked once. */
  onFoundWhere: (found: ShopLocation) => void;
  onClose: () => void;
}) {
  const paid = lastPaid(entry.name, items);
  // The same string the shop buttons search for, so a price is a price for the
  // thing the button beside it will actually find.
  const [locating, setLocating] = useState(false);
  const [asked, setAsked] = useState(() => introSeen(householdId));
  /**
   * The doorway, not a footnote.
   *
   * Shown until the household has an answer, whatever that answer was. Comparing
   * five shops is the biggest thing this screen does and it used to hide behind
   * a link in the corner — see PriceIntro.
   */
  const introDue = !where && !asked;
  /**
   * Already introduced, but this household has no city — a second account on
   * the same phone, or a second household. Country-wide prices, not silence:
   * a feature that is present but quiet reads as absent (see `introSeen`).
   */
  const effectiveWhere = where ?? (asked ? ANYWHERE_IN_INDIA : null);

  const prices = useShopPrices(searchQuery(entry), effectiveWhere);
  const links = storeLinks(entry);
  // The row is sorted by price, so the first card is the one to buy.
  const best = prices.cards[0] ?? null;

  async function locate() {
    setLocating(true);
    try {
      const found = await findWhere();
      // A refusal is not a rejection of the feature, so it falls back to the
      // country rather than switching prices off.
      onFoundWhere(found ?? ANYWHERE_IN_INDIA);
    } finally {
      markIntroSeen(householdId);
      setAsked(true);
      setLocating(false);
    }
  }

  function skip() {
    markIntroSeen(householdId);
    setAsked(true);
    onFoundWhere(ANYWHERE_IN_INDIA);
  }

  /**
   * The pack size, if the household wrote one down.
   *
   * Deliberately NOT the matched listing's name, rating or picture. Those
   * describe one offer out of a dozen, and hoisting them into the heading
   * turned "the packet we happened to price" into "your item": "milk" became a
   * particular carton, and even "hajmola" became the candy pouch. They live on
   * the cards now, where each belongs to the offer it came from.
   *
   * Nor why the line is here. "You ran out" earns its place on the LIST, where
   * it explains a row you may not remember adding; on the line itself you are
   * already looking at the thing.
   */
  const meta = [entry.sizeText].filter(Boolean);

  return (
    <section aria-label="buying this" className="detail">
      {/* ── What it is ───────────────────────────────────────────────────
          The picture, the pack and the score sit at the TOP, with the name.
          They were in the buy card under a "Buy it" heading, which put the
          identity of the thing below the list of places to get it — a screen
          quoting prices for a product it had not yet shown you. */}
      <header className="detail-head">
        <button
          type="button"
          className="back"
          data-testid="back-btn"
          onClick={onClose}
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
        </button>
        <div className="detail-ident">
          <h2 data-testid="cart-title">{entry.name}</h2>
          {meta.length > 0 && <p className="detail-sub">{meta.join(" · ")}</p>}
        </div>
        {entry.qty > 1 && <span className="when">×{entry.qty}</span>}
      </header>

      <div className="card">
        {/* Choose the packet, the shop and the price — see OfferCards. */}
        <OfferCards cards={prices.cards} status={prices.status} source={prices.source} />

        {/* `target="_blank"` and not an in-page navigation: from an installed
            app this hands the URL to the OS, which is what lets the store's own
            app claim it. `noreferrer` as well as `noopener` — a shop has no
            business knowing which screen of a fridge app sent someone. */}
        {/* The five apps, plain — a way in when the cards are empty, and the
            only way to reach Blinkit or Instamart, which frequently list
            nothing on Google Shopping at all. Prices used to sit here too;
            they are said once now, on the cards. */}
        <div className="stores" data-testid="stores">
          {links.map((l) => {
            const span = prices.spans.get(l.id);
            return (
              <a
                key={l.id}
                className="store"
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`store-${l.id}`}
              >
                {/* The shop's own app icon, so the one you always use is found
                  without reading five words. Served from our own origin — see
                  StoreMark for why it is not hot-linked. */}
                <StoreMark id={l.id} />
                <span className="store-name">
                  {l.label}
                  {/* What this shop's listings SPAN, not one number. A shop's
                    offers for "milk" run from a 200ml carton to a six-pack, and
                    quoting the lowest as "the price here" invites a tap that
                    finds something else. A range is the honest shape — and a
                    wide one usefully says "they stock several sizes". */}
                  {span && (
                    <span className="store-price" data-testid={`span-${l.id}`}>
                      {span.low === span.high
                        ? formatMoney(span.low)
                        : `${formatMoney(span.low)} – ${formatMoney(span.high)}`}
                    </span>
                  )}
                </span>
                <span className="store-go" aria-hidden="true">
                  ↗
                </span>
              </a>
            );
          })}
        </div>

        {/* Only when we actually know. No shop tells us what YOU paid, and it
            is the number that makes a price on a screen good or bad. */}
        {paid && (
          <p className="note" data-testid="last-paid">
            You paid <strong>{formatMoney(paid.minor)}</strong> ·{" "}
            {formatIsoDate(isoFromMillis(paid.at, timezone))}
          </p>
        )}

        {/* The upgrade, and only in the state that needs it: prices are being
            quoted for the whole country because nobody gave a city. Quick
            commerce is per-pincode, so this genuinely changes the answer. */}
        {isCountryWide(where) && (
          <button
            type="button"
            className="ghost"
            disabled={locating}
            onClick={locate}
            data-testid="use-my-location"
          >
            {locating ? "Looking…" : "Prices for India — use my location"}
          </button>
        )}
      </div>

      <Nutrition entry={entry} owned={items} />

      {/* ── What to do about it ──────────────────────────────────────────
          Pinned. Buying leads when we know where is cheapest, because that is
          the act this screen was opened for; "Got it" is the follow-up, hours
          later, when the packet is actually in the house. Without prices there
          is nothing to lead with, so "Got it" takes the top slot back. */}
      <div className="detail-actions">
        {best ? (
          <a
            className="primary buynow"
            href={best.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="buy-now"
          >
            {best.store && <StoreMark id={best.store} />}
            {/* Names the DESTINATION, not the intention. "Buy now" alone hides
                which of a dozen offers the tap is about to open. It is always
                the first card, which is always the cheapest. */}
            <span className="buynow-text">Buy on {sellerName(best)}</span>
            <span className="buynow-price">{formatMoney(best.priceMinor)}</span>
          </a>
        ) : null}
        <div className="cart-actions">
          {/* A question, because that is what it is: the app does not know
              whether you bought it, and "Got it" read as an instruction from
              the app rather than an answer from you. Naming the destination —
              the larder — also says what the tap does next, which is open the
              form that asks for a date and a shelf. */}
          <button
            type="button"
            className={best ? "secondary" : "primary"}
            disabled={busy}
            onClick={onGot}
            data-testid="cart-got"
          >
            Purchased? Add to larder.
          </button>
          {/* A bin, and small. Taking something off the list is the rarest act
              on this screen and the only destructive one; a word-sized button
              beside it would give it weight it has not earned, and a big
              target for a delete is a mis-tap waiting to happen. */}
          <button
            type="button"
            className="binbtn"
            disabled={busy}
            onClick={onDrop}
            aria-label={`Remove ${entry.name} from your shopping list`}
            data-testid="cart-drop"
          >
            <span aria-hidden="true">🗑</span>
          </button>
        </div>
      </div>

      {introDue && <PriceIntro busy={locating} onAllow={locate} onSkip={skip} />}
    </section>
  );
}

/**
 * What is in it — the same panel the item screen leads with.
 *
 * A thing on the shopping list is a thing you are about to bring home, which is
 * exactly when "how much food is this" is a decision rather than trivia. The
 * numbers come from a packet the household has had before or from the global
 * product cache, never from a new lookup — see `useProductFacts`.
 */
function Nutrition({ entry, owned }: { entry: CartEntry; owned: readonly Known[] }) {
  const facts = useProductFacts(entry.name, entry.brand, entry.sizeText, owned);
  if (!facts) return null;
  return (
    <div className="card" data-testid="cart-nutrition">
      <h3 className="card-title">What's in it</h3>
      <NutritionPanel nutrition={facts.nutrition} size={facts.size} />
    </div>
  );
}

/**
 * "152.2k" rather than "152,214".
 *
 * Nobody reads a review count as a number; they read it as a size. Three
 * characters says the same thing as seven and leaves room for the rest of the
 * line on a phone.
 */
function compact(n: number): string {
  if (n >= 100_000) return `${Math.round(n / 100_000)}L`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
}
